import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';

import { cancelarSecretario, contratarSecretario, fetchEscritorios } from '@/api/client';
import { addonDisponivel, useOfertaSecretario, type Intervalo } from '@/components/assinatura/SecretarioAddonCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useNotification } from '@/context/NotificationProvider';
import type { SubscriptionData } from '@/hooks/useSubscription';
import { notifyMutationError } from '@/lib/errors';
import { formatDate, formatPrice } from '@/lib/formatters';
import { queryKeys } from '@/lib/pwa/queryConfig';

type Acao = { tipo: 'contratar' | 'cancelar' | 'manter'; escritorioId: string; nome: string };

/**
 * O Secretário de cada escritório em que o usuário é o Proprietário
 * (docs/PLANO_SECRETARIO_ESCRITORIO.md §7): um add-on por escritório, na assinatura
 * do Proprietário — mais um na mesma linha da fatura. Só aparece para quem é
 * Proprietário de algum escritório e já paga um plano.
 */
export function SecretarioDosEscritorios({
  subscription,
  stripeReady,
}: {
  subscription: SubscriptionData | null | undefined;
  stripeReady: boolean;
}) {
  const { data: oferta } = useOfertaSecretario();
  const { data: escritorios } = useQuery({ queryKey: queryKeys.escritorios.all, queryFn: fetchEscritorios });
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [acao, setAcao] = useState<Acao | null>(null);
  const [enviando, setEnviando] = useState(false);

  const meus = (escritorios ?? []).filter((e) => e.meu_role === 'PROPRIETARIO');
  const assinaturaPaga =
    !!subscription?.gateway_subscription_id && ['ATIVA', 'PAST_DUE'].includes(subscription.status);
  if (!oferta || meus.length === 0) return null;

  const intervalo: Intervalo = subscription?.plano?.intervalo === 'ANUAL' ? 'ANUAL' : 'MENSAL';
  const centavos = intervalo === 'ANUAL' ? oferta.preco_anual_centavos : oferta.preco_mensal_centavos;
  const podeContratar =
    assinaturaPaga && subscription?.status !== 'PAST_DUE' && stripeReady && addonDisponivel(oferta, intervalo);

  const executar = async () => {
    if (!acao) return;
    setEnviando(true);
    try {
      if (acao.tipo === 'cancelar') {
        await cancelarSecretario(acao.escritorioId);
        showSuccess(`Secretário de ${acao.nome} cancelado`);
      } else {
        await contratarSecretario(acao.escritorioId);
        showSuccess(`Secretário de ${acao.nome} na sua assinatura`);
      }
      await queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      await queryClient.invalidateQueries({ queryKey: ['secretario'] });
    } catch (err) {
      notifyMutationError(err, showError);
    } finally {
      setEnviando(false);
      setAcao(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Building2 className="h-4 w-4" />
          Secretário dos seus escritórios
        </CardTitle>
        <CardDescription>
          Um Secretário por escritório, que atende em nome dos advogados que você escolher. O add-on é cobrado na
          sua assinatura: {formatPrice(centavos)}/{intervalo === 'ANUAL' ? 'ano' : 'mês'} por escritório.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {meus.map((e) => {
          const addon = subscription?.addons?.find((a) => a.codigo === 'secretario' && a.escritorio_id === e.id);
          const cancelado = addon?.origem === 'STRIPE' && !!addon.expira_em;
          return (
            <div
              key={e.id}
              className="flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <p className="truncate font-medium">{e.nome}</p>
                {addon?.origem === 'CORTESIA' ? (
                  <Badge variant="secondary">Incluído</Badge>
                ) : cancelado ? (
                  <p className="text-xs text-muted-foreground">Cancelado — vale até {formatDate(addon.expira_em)}</p>
                ) : addon ? (
                  <Badge variant="outline" className="border-emerald-600/40 text-emerald-700 dark:text-emerald-400">
                    Contratado
                  </Badge>
                ) : (
                  <p className="text-xs text-muted-foreground">Sem o Secretário</p>
                )}
              </div>
              {addon?.origem !== 'CORTESIA' &&
                (cancelado ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="min-h-11"
                    onClick={() => setAcao({ tipo: 'manter', escritorioId: e.id, nome: e.nome })}
                  >
                    Manter
                  </Button>
                ) : addon ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="min-h-11"
                    onClick={() => setAcao({ tipo: 'cancelar', escritorioId: e.id, nome: e.nome })}
                  >
                    Cancelar
                  </Button>
                ) : (
                  <Button
                    type="button"
                    className="min-h-11"
                    disabled={!podeContratar}
                    onClick={() => setAcao({ tipo: 'contratar', escritorioId: e.id, nome: e.nome })}
                  >
                    Contratar
                  </Button>
                ))}
            </div>
          );
        })}
        {!assinaturaPaga && (
          <p className="text-xs text-muted-foreground">Assine um plano para contratar o Secretário do escritório.</p>
        )}
      </CardContent>
      <ConfirmDialog
        open={acao !== null}
        title={
          acao?.tipo === 'cancelar'
            ? `Cancelar o Secretário de ${acao.nome}?`
            : acao?.tipo === 'manter'
              ? `Manter o Secretário de ${acao?.nome}?`
              : `Contratar o Secretário de ${acao?.nome}?`
        }
        message={
          acao?.tipo === 'cancelar'
            ? 'Ele para de cobrar agora, sem crédito do período pago, e atende até o fim dele.'
            : acao?.tipo === 'manter'
              ? 'Ele volta para a sua assinatura e segue atendendo depois deste período.'
              : `Entra na sua assinatura agora: ${formatPrice(centavos)} por ${intervalo === 'ANUAL' ? 'ano' : 'mês'}, com o proporcional deste período cobrado já.`
        }
        confirmLabel={acao?.tipo === 'cancelar' ? 'Cancelar o Secretário' : 'Confirmar'}
        confirmColor={acao?.tipo === 'cancelar' ? 'error' : 'primary'}
        loading={enviando}
        onConfirm={executar}
        onCancel={() => setAcao(null)}
      />
    </Card>
  );
}
